import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash2, Loader2, TriangleAlert, Globe } from "lucide-react";

/**
 * Delete controls for the platform admin, driven by the area registry in
 * backend/admin_cleanup.py. Everything deleted here is snapshotted into the
 * 30-day recycle bin (/admin/cleanup → Recycle bin) and written to the audit log.
 */

export async function purgeArea(area, payload) {
  const r = await api.post(`/admin/cleanup/areas/${area}/delete`, payload);
  return r.data;
}

/** Bin icon for a single row. */
export const RowBin = ({ area, id, name, onDone, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    try {
      await purgeArea(area, { mode: "ids", ids: [id] });
      toast.success("Deleted — recoverable for 30 days in Data Cleanup");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              title="Delete this row"
              className={`grid size-7 place-items-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 ${className}`}
              data-testid={`purge-row-${area}-${id}`}>
        <Trash2 className="size-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="purge-row-dialog">
          <DialogHeader>
            <DialogTitle>Delete this row?</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-slate-900">{name || id}</span> will be removed from the
              list. It's snapshotted to the recycle bin for 30 days, so you can put it back.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="purge-row-cancel">Cancel</Button>
            <Button onClick={go} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="purge-row-confirm">
              {busy ? <Loader2 className="size-4 animate-spin" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** "Clear this area" — the test rows, or the lot. */
export const ClearArea = ({ area, onDone, label = "Clear this area" }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("simulated");
  const [busy, setBusy] = useState(null);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) { setTyped(""); return; }
    api.get("/admin/cleanup/areas")
      .then((r) => {
        setInfo((r.data.areas || []).find((a) => a.key === area) || null);
        setMode(r.data.public_mode || "simulated");
      })
      .catch(() => setInfo(null));
  }, [open, area]);

  const run = async (mode) => {
    setBusy(mode);
    try {
      const d = await purgeArea(area, mode === "all" ? { mode: "all", confirm: "DELETE" } : { mode: "test" });
      toast.success(d.total ? `${d.total.toLocaleString()} rows deleted — recoverable for 30 days` : "Nothing to delete");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
              className="rounded-full border-slate-300 text-slate-600 hover:border-red-300 hover:text-red-600"
              data-testid={`clear-area-${area}`}>
        <Trash2 className="mr-1.5 size-3.5" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="clear-area-dialog">
          <DialogHeader>
            <DialogTitle>{info?.label || "Clear this area"}</DialogTitle>
            <DialogDescription>{info?.note}</DialogDescription>
          </DialogHeader>

          {!info ? (
            <div className="grid place-items-center py-8 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["In here", info.total], ["Obvious test rows", info.test], ["Real-looking", info.real]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <div className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-slate-400">{l}</div>
                    <div className="mt-0.5 font-mono text-[19px] font-semibold tabular-nums text-slate-900">{v.toLocaleString()}</div>
                  </div>
                ))}
              </div>

              {info.public && (
                <div className="flex items-start gap-2 rounded-xl border border-[#0055FF]/30 bg-[#0055FF]/[0.06] p-3 text-[12.5px] text-slate-700"
                     data-testid="clear-area-public-warning">
                  <Globe className="mt-0.5 size-4 shrink-0 text-[#0055FF]" />
                  {mode === "simulated" ? (
                    <span>
                      The public website can count these rows — but your live numbers are on
                      <b> simulated</b> right now, so clearing this changes nothing on connex.is.
                      It would matter the day you switch the public activity mode to blended or real.
                    </span>
                  ) : (
                    <span>
                      Your public numbers are on <b>{mode}</b>, so these rows are feeding the
                      counters on connex.is. Empty this and they drop until real activity replaces them.
                    </span>
                  )}
                </div>
              )}
              {info.also_total > 0 && (
                <p className="text-[12px] text-slate-500">
                  Clearing everything also removes {info.also_total.toLocaleString()} linked rows
                  ({Object.keys(info.also).join(", ")}).
                </p>
              )}

              <div className="space-y-2 pt-1">
                {info.test > 0 ? (
                  <Button onClick={() => run("test")} disabled={!!busy}
                          className="w-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="clear-area-test">
                    {busy === "test" ? <Loader2 className="size-4 animate-spin" />
                      : `Delete the ${info.test.toLocaleString()} obvious test row${info.test === 1 ? "" : "s"}`}
                  </Button>
                ) : (
                  <p className="text-[12.5px] text-slate-500" data-testid="clear-area-no-test">
                    Nothing in here looks like test data — no row mentions test, demo, sample or a
                    demo account. Use Review on this area to pick rows by hand, or empty it below.
                  </p>
                )}
                <div className="rounded-xl border border-red-200 bg-red-50/50 p-3">
                  <div className="flex items-start gap-2 text-[12.5px] text-red-800">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                    <span>Or empty it completely — all {info.total.toLocaleString()} rows, real ones included.
                    Type DELETE to unlock.</span>
                  </div>
                  <div className="mt-2.5 flex gap-2">
                    <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE"
                           className="h-9 font-mono" data-testid="clear-area-confirm-input" />
                    <Button onClick={() => run("all")} disabled={typed.trim().toUpperCase() !== "DELETE" || !!busy}
                            className="h-9 shrink-0 bg-red-600 hover:bg-red-700" data-testid="clear-area-all">
                      {busy === "all" ? <Loader2 className="size-4 animate-spin" /> : "Delete everything"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
